import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader } from 'lucide-react';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [paymentStatus, setPaymentStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    // Clear any lingering checkout data from sessionStorage
    sessionStorage.removeItem('cardCustomization');
    
    // Poll payment status
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('failed');
      setError('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await apiRequest(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setOrderDetails(data);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('failed');
        setError('Payment session expired. Please try again.');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPaymentStatus('failed');
      setError('Error checking payment status. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-3xl p-12 shadow-lg text-center">
          {paymentStatus === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-navy mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-navy mb-4">
                Processing Your Payment...
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {paymentStatus === 'success' && (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-navy mb-4">
                Payment Successful!
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Thank you for your order. Your review card is being prepared.
              </p>

              {orderDetails && (
                <div className="bg-gray-50 rounded-2xl p-8 mb-8 text-left max-w-md mx-auto">
                  <h2 className="text-xl font-bold text-navy mb-4">Order Summary</h2>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex justify-between">
                      <span>Order ID:</span>
                      <span className="font-semibold">{sessionId.slice(0, 16)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Amount Paid:</span>
                      <span className="font-semibold">
                        {orderDetails.currency?.toUpperCase()} {(orderDetails.amount_total / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payment Status:</span>
                      <span className="font-semibold text-green-600">Paid</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-gray-600 mb-8">
                <p className="mb-2">You will receive an order confirmation email shortly.</p>
                <p>Your review cards will be shipped within 5-7 business days.</p>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-6"
                >
                  Back to Home
                </Button>
                <Button
                  onClick={() => navigate('/review-cards')}
                  className="bg-gray-200 text-navy hover:bg-gray-300 rounded-full px-8 py-6"
                >
                  Order More Cards
                </Button>
              </div>
            </>
          )}

          {paymentStatus === 'failed' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="text-4xl font-bold text-navy mb-4">
                Payment Issue
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {error || 'We encountered an issue with your payment.'}
              </p>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/review-cards')}
                  className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-6"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-gray-200 text-navy hover:bg-gray-300 rounded-full px-8 py-6"
                >
                  Back to Home
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
